import { useState } from "react";
import { Button } from "@mui/material";
import { useTranslation } from "@privacy/translations";
import { DynamicGrid } from "../dynamicGrid";
import { PageTitle } from "../shared/Page";
import { EmptyState } from "../shared/EmptyState";
import { usePrivacyDocumentTitle } from "../shared/usePrivacyDocumentTitle";
import { useNavigateWithQueryParams } from "../shared/navigateWithQueryParams";
import { AssessmentDrawer } from "./AssessmentDrawer/AssessmentDrawer";
import { TrustPageHeader } from "./TrustHeader";
import { TrustCardContent } from "./TrustGridCard";
import { useQuery } from "@tanstack/react-query";
import { assessmentFormStandardAnswersQueryOptions, assessmentsGridViewQueryOptions } from "./trustAPI";
import { TRUST_PATHS } from "./paths";
import { TrustSignDialog } from "./TrustSignDialog";
import { StandardAnswersDrawer } from "./StandardAnswersDrawer";
import { queryClient } from "../api/queryClient";

const TrustGridPage = () => {
  usePrivacyDocumentTitle("Trust.Page.Title");
  const { t } = useTranslation();
  const navigate = useNavigateWithQueryParams();

  const assessmentsGridViewQuery = useQuery(assessmentsGridViewQueryOptions());
  const [signDialogState, setSignDialogState] = useState<{ open: false } | { open: true; assessmentId: string }>({
    open: false,
  });

  const openAssessmentDrawer = (id: string) => navigate(TRUST_PATHS.assessment(id));

  return (
    <>
      <DynamicGrid.CardList
        rowsData={assessmentsGridViewQuery.data?.assessments}
        extraHeightOffset={TrustPageHeader.HEIGHT}
        getRowId={row => row.id}
        card={row => ({
          content: (
            <TrustCardContent
              row={row}
              onCustomizeAnswersClick={() => openAssessmentDrawer(row.id)}
              onSeeSentAnswersClick={() => openAssessmentDrawer(row.id)}
              onSendStandardAnswersClick={() => setSignDialogState({ open: true, assessmentId: row.id })}
            />
          ),
          clickable: row.status !== "withdrawn",
          searchableValue: `${row.requestedBy.organisation.name} ${row.requestedBy.user.firstName} ${row.requestedBy.user.lastName}`,
        })}
        toolbarTabs={{
          queryKey: "status",
          filter: (row, activeTab) => (activeTab === "all" ? true : row.status === activeTab),
          tabs: [
            { labelKey: "Trust.Tab.Filter.All", value: "all" },
            { labelKey: "Trust.Tab.Filter.AwaitingResponse", value: "awaiting-response" },
            { labelKey: "Trust.Tab.Filter.Responded", value: "responded" },
          ],
        }}
        Toolbar={TrustToolbar}
        NoRowsOverlay={NoTrustRowsOverlay}
        onRowClick={openAssessmentDrawer}
        texts={{ searchFieldPlaceholder: t("Trust.SearchPlaceholder") }}
      />
      {signDialogState.open && (
        <TrustSignDialog
          assessmentId={signDialogState.assessmentId}
          onClose={() => setSignDialogState({ open: false })}
          onSigned={() => setSignDialogState({ open: false })}
        />
      )}
    </>
  );
};

const TrustToolbar = () => {
  const { t } = useTranslation();
  const navigate = useNavigateWithQueryParams();
  return (
    <DynamicGrid.Toolbar
      pageTitle={<PageTitle title={t("Trust.Page.Title")} subTitle={t("Trust.Page.SubTitle")} />}
      actionButtons={null}
      filterButtons={
        <Button
          variant="text"
          sx={{ whiteSpace: "nowrap" }}
          size="small"
          onClick={() => navigate(TRUST_PATHS.standardAnswers())}
        >
          {t("Trust.StandardAnswers.ViewButton")}
        </Button>
      }
    />
  );
};

const NoTrustRowsOverlay = () => <EmptyState labelKey="Trust.NoRows" />;

export const trustGridPageRoutes = DynamicGrid.setupRoutes({
  gridElement: <TrustGridPage />,
  gridPrefetcher: () => {
    queryClient.prefetchQuery(assessmentsGridViewQueryOptions());
    queryClient.prefetchQuery(assessmentFormStandardAnswersQueryOptions());
  },
  drawerRoutes: [
    {
      path: "standard-answers",
      element: <StandardAnswersDrawer />,
    },
  ],
  rowDrawerRoutes: [{ index: true, element: <AssessmentDrawer /> }],
});
